import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';

/**
 * Time a quiz callable's round-trip (tap → response) and report it to GA4 as a
 * `quiz_latency` event, so we can see — for REAL users — how fast questions,
 * answers, and next-questions actually load (p50/p95, warm vs cold), rather than
 * guessing. `value` carries the latency in ms (GA4 event value → averageable);
 * `call` segments by which callable; `cold` flags a likely gen2 cold start.
 *
 * Fire-and-forget telemetry: a logging failure must never break the quiz flow,
 * and the measured call's result/throw passes through unchanged.
 */
const COLD_START_MS = 2500;

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

export async function trackLatency<T>(call: string, fn: () => Promise<T>): Promise<T> {
  const start = now();
  let ok = true;
  try {
    return await fn();
  } catch (error) {
    ok = false;
    throw error;
  } finally {
    const value = Math.round(now() - start);
    try {
      logAnalyticsEvent(ANALYTICS_EVENTS.QUIZ_LATENCY, {
        call,
        value,
        ok,
        cold: value > COLD_START_MS,
      });
    } catch {
      /* telemetry is best-effort — never surface it to the user */
    }
  }
}
